import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import logo from '../assets/images/logo.png';
import { APP_STORE_URL } from '../constants/storeUrls';
import { trackEvent, WEB_STORE_CLICK } from '../lib/analytics';
import { isIos } from '../lib/inAppBrowser';
import { useSEO } from '../hooks/useSEO';
import styles from './GetApp.module.css';

async function logIosReferralClick(params: {
  referralCode: string;
  partnerId: string;
  campaignId: string | null;
}): Promise<void> {
  const body = {
    screenWidth: screen.width,
    screenHeight: screen.height,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    locale: navigator.language,
    referralCode: params.referralCode,
    partnerId: params.partnerId,
    campaignId: params.campaignId,
    isCampaignLink: Boolean(params.campaignId),
  };
  try {
    await fetch('/api/logIosClick', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    });
  } catch {
    // Still open App Store even if attribution logging fails.
  }
}

export function GetApp() {
  const [searchParams] = useSearchParams();
  const [busy, setBusy] = useState(false);

  const ref = (searchParams.get('ref') || '').trim();
  const partnerId = (searchParams.get('pid') || '').trim();
  const campaignId = (searchParams.get('cid') || '').trim() || null;

  useSEO({
    title: 'Get LuvLust on your phone',
    description:
      "Instagram's in-app browser blocks App Store links. Continue to download LuvLust on the App Store.",
  });

  const handleContinue = useCallback(
    async (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      if (busy) return;
      setBusy(true);

      void trackEvent(WEB_STORE_CLICK, {
        platform: 'ios',
        location: 'get_bridge',
        ref_code: ref || undefined,
      });

      if (isIos() && ref && partnerId) {
        await logIosReferralClick({
          referralCode: ref,
          partnerId,
          campaignId,
        });
      }

      window.location.href = APP_STORE_URL;
    },
    [busy, campaignId, partnerId, ref],
  );

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <img className={styles.logo} src={logo} alt="LuvLust" width={88} height={88} />
        <p className={styles.brand}>luvlust</p>
        <h1 className={styles.headline}>Get luvlust on your phone</h1>
        <p className={styles.body}>
          Instagram&apos;s in-app browser blocks App Store links. Tap below and we&apos;ll open Safari
          or Chrome for you.
        </p>
        <a
          className={styles.cta}
          href={APP_STORE_URL}
          onClick={handleContinue}
          aria-disabled={busy}
        >
          {busy ? 'Opening…' : 'Continue to App Store'}
        </a>
        <p className={styles.tip}>If nothing happens, tap ••• → Open in external browser</p>
      </div>
    </div>
  );
}
